import { useEffect, useMemo, useState } from 'react';
import { reference, type LocalStop } from '../lib/localdb.js';
import { addCapture } from '../lib/actions.js';
import { apiJson, ApiError } from '../lib/api.js';
import { amount, text } from '../lib/format.js';
import { BigButton, Chip, Sheet, SignaturePad } from '../components/Controls.js';

/**
 * The quote at the door.
 *
 * `sales` is the largest single call reason in the history — 522 of 1 054 classified calls —
 * and the word *soumission* appears constantly. A technician who has just opened a unit, or
 * photographed a hazard, is the best-placed person in the company to quote the next job.
 *
 * Three constraints shape the screen, and all three are the permission model made visible:
 *
 *   - **Priced from the catalogue.** There is no field to type a price into. The imported
 *     permissions denied every field employee the creation of a quote without exception; this
 *     is an explicit, opted-into exception and it does not extend to naming a number.
 *   - **Drafted, never issued.** The server forces the status. Issuing stays with the office,
 *     or with whoever the business has expressly delegated it to.
 *   - **Accepted at the door, with a signature.** A quote accepted on the driveway is an
 *     installation booked this week rather than a PDF chased for a month — and acceptance
 *     without a signature is a claim, so the control is not offered without one.
 *
 * This is the one field flow that genuinely needs signal, and says so: the total has to come
 * from the server's own arithmetic, and a quote whose total the technician cannot show the
 * customer is worse than one raised from the van twenty minutes later.
 */

type Product = { id: string; kind: string; code: string | null; name: unknown; unitPrice: string; unitOfMeasure: string | null };
type Requirement = { id: string; name: unknown; costProductId?: string | null };

export function QuoteSheet({
  open,
  stop,
  onClose,
}: {
  open: boolean;
  stop: LocalStop;
  onClose: () => void;
}) {
  const [products, setProducts] = useState<Product[]>([]);
  const [chosen, setChosen] = useState<Map<string, number>>(new Map());
  const [search, setSearch] = useState('');
  const [signing, setSigning] = useState(false);
  const [acceptanceId, setAcceptanceId] = useState<string | null>(null);
  const [acceptedName, setAcceptedName] = useState('');
  const [result, setResult] = useState<{ number: string; total: string; status: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    void (async () => {
      setProducts(await reference<Product[]>('products', []));
      void (await reference<Requirement[]>('accessRequirements', []));
    })();
  }, [open]);

  const matches = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const list = products.filter((product) => product.kind !== 'bundle' || true);
    if (!needle) return list.slice(0, 20);
    return list
      .filter(
        (product) =>
          text(product.name).toLowerCase().includes(needle) || (product.code ?? '').toLowerCase().includes(needle),
      )
      .slice(0, 20);
  }, [products, search]);

  const total = useMemo(
    () =>
      [...chosen.entries()].reduce((sum, [id, quantity]) => {
        const product = products.find((candidate) => candidate.id === id);
        return sum + Number(product?.unitPrice ?? 0) * quantity;
      }, 0),
    [chosen, products],
  );

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const quote = await apiJson<{ number: string; total: string; status: string }>('/quotes', {
        method: 'POST',
        headers: { 'idempotency-key': crypto.randomUUID() },
        body: JSON.stringify({
          jobId: stop.job.id,
          appointmentId: stop.id,
          items: [...chosen.entries()].map(([productId, quantity]) => ({ productId, quantity })),
          acceptanceAttachmentId: acceptanceId,
          acceptedName: acceptedName || stop.customer.name,
        }),
      });
      setResult(quote);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Sans signal, la soumission se prépare au retour — le total doit venir du catalogue.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} title="Soumission" onClose={onClose}>
      {result ? (
        <div className="quote__done">
          <p className="reading__value tnum">{amount(result.total)}</p>
          <p className="quiet">
            {result.status === 'accepted' ? 'Acceptée' : 'Brouillon'} {result.number} — l’office la finalise et
            l’envoie. Rien n’est transmis au client d’ici.
          </p>
          <BigButton tone="quiet" onClick={onClose}>
            Fermer
          </BigButton>
        </div>
      ) : (
        <>
          <label className="field">
            <span>Chercher dans le catalogue</span>
            <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Thermopompe, filtre…" />
          </label>

          <div className="chips">
            {matches.map((product) => (
              <Chip
                key={product.id}
                on={chosen.has(product.id)}
                onToggle={() =>
                  setChosen((current) => {
                    const next = new Map(current);
                    if (next.has(product.id)) next.delete(product.id);
                    else next.set(product.id, 1);
                    return next;
                  })
                }
              >
                {text(product.name)} · {amount(product.unitPrice)}
              </Chip>
            ))}
            {matches.length === 0 && <p className="quiet">Aucun article ne correspond.</p>}
          </div>

          {chosen.size > 0 && (
            <>
              <ul className="owing">
                {[...chosen.entries()].map(([id, quantity]) => {
                  const product = products.find((candidate) => candidate.id === id);
                  if (!product) return null;
                  return (
                    <li key={id}>
                      <span>
                        {text(product.name)} ×{quantity}
                      </span>
                      <span className="tnum">{amount((Number(product.unitPrice) * quantity).toFixed(2))}</span>
                    </li>
                  );
                })}
              </ul>
              {/* Before tax, and said so: the server computes the real total from the rates. */}
              <p className="reading__value tnum">{amount(total.toFixed(2))}</p>
              <p className="quiet">Avant taxes. Le total exact est calculé par le bureau.</p>
            </>
          )}

          {/* Rendered in the customer's language, because they are the one reading it. */}
          {chosen.size > 0 && (
            <section className="notice" lang={stop.customer.locale}>
              <h4>{stop.customer.locale === 'en' ? 'Acceptance' : 'Acceptation'}</h4>
              {acceptanceId ? (
                <p className="quiet">Signé par {acceptedName || stop.customer.name}.</p>
              ) : signing ? (
                <SignaturePad
                  label={`${stop.customer.name} — ${stop.customer.locale === 'en' ? 'accepts this quote' : 'accepte cette soumission'}`}
                  onDone={async (blob) => {
                    const saved = await addCapture({
                      appointmentId: stop.id,
                      kind: 'signature',
                      blob,
                      caption: `Soumission — ${acceptedName || stop.customer.name}`,
                      withPosition: true,
                    });
                    setAcceptanceId(saved.id);
                    setSigning(false);
                  }}
                />
              ) : (
                <>
                  <label className="field">
                    <span>Nom du signataire</span>
                    <input
                      value={acceptedName}
                      onChange={(event) => setAcceptedName(event.target.value)}
                      placeholder={stop.customer.name}
                    />
                  </label>
                  <BigButton tone="quiet" onClick={() => setSigning(true)}>
                    Faire accepter maintenant
                  </BigButton>
                </>
              )}
            </section>
          )}

          {error && <p className="warn">{error}</p>}

          <BigButton tone="positive" disabled={chosen.size === 0} busy={busy} onClick={() => void submit()}>
            {acceptanceId ? 'Enregistrer acceptée' : 'Enregistrer le brouillon'}
          </BigButton>
        </>
      )}
    </Sheet>
  );
}
